import moment from 'moment';
import React, { useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import { KeyboardDateTimePicker } from '@material-ui/pickers';

import useGlobalSnackBar from '../../../hooks/useGlobalSnackBar';
import apiService from '../../../services/api.service';
import MyButton from '../../../wrappers/Button';
import CustomCheckbox from '../../parts/CustomCheckbox';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    backgroundColor: theme.palette.modal.backgroundColor,
    position: 'relative',
    flexDirection: 'column',
    padding: theme.spacing(3),
    size: 'sm',
  },
  contentContainer: {
    overflowY: 'auto',
    marginTop: '0px !important',
  },
  closeButton: {
    position: 'absolute',
    top: 3,
    right: 3,
  },
  divider: {
    marginRight: 12,
  },
  section: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
    },
    '& .MuiCheckbox- root': {
      backgroundColor: '#fff',
    },
  },
  readOnlySection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    '& .MuiInputBase-input': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'rgba(0, 0, 0, 0.2)',
      borderBottomStyle: 'dotted',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottomColor: 'rgba(0, 0, 0, 0.2)',
      borderBottomStyle: 'dotted',
    },
    '& .MuiInput-underline.Mui-disabled:before': {
      borderBottomColor: 'rgba(0, 0, 0, 0.2)',
      borderBottomStyle: 'dotted',
    },
    '& .MuiFormLabel-root': {
      fontSize: '0.875rem',
      color: 'rgba(0, 0, 0, 0.54)',
    },
  },
  checkboxWrapper: {
    '& .MuiFormControlLabel-root': {
      marginLeft: 0,
    },
  },
  checkbox: {
    padding: 0,
  },
  infoBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f5f0ec',
    borderLeft: `4px solid #8b7355`,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  infoIcon: {
    color: '#8b7355',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '2px',
  },
  infoText: {
    color: '#5d5d5d',
    fontSize: '14px',
    lineHeight: '1.6',
    margin: 0,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    padding: theme.spacing(0.5),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  auditLogHeader: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 0.5),
    background: '#e1e1e1',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      borderRadius: '4px',
    },
  },
  auditLogTitle: {
    fontSize: '16px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginLeft: 10,
    marginRight: 10,
  },
  auditLogContent: {
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    maxHeight: '160px',
    overflowY: 'auto',
    background: '#ffffff',
  },
  auditLogItem: {
    marginBottom: theme.spacing(1),
    fontSize: '14px',
    lineHeight: '1.6',
  },
  auditLogTimestamp: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    verticalAlign: 'top',
  },
  auditLogMessage: {
    maxWidth: '550px',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    display: 'inline-block',
  },
  auditLogProperty: {
    fontStyle: 'italic',
  },
  buttonsBox: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  buttons: {
    marginRight: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

function LandDeliveryModal({ open, onClose, shipmentNumber, containerNumber, themeName = 'light' }) {
  const classes = useStyles();
  const [clientRequestedDeliveryDate, setClientRequestedDeliveryDate] = useState(null);
  const [emptyReady, setEmptyReady] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [auditLogExpanded, setAuditLogExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deliveryData, setDeliveryData] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const { showSuccessSnackBar } = useGlobalSnackBar();

  // Track initial values for change detection
  const [initialValues, setInitialValues] = useState({
    clientRequestedDeliveryDate: null,
    emptyReady: false,
    urgent: false,
  });

  // Fetch land delivery details when modal opens
  useEffect(() => {
    console.log([open, shipmentNumber, containerNumber]);
    const fetchLandDeliveryDetails = async () => {
      setLoading(true);
      try {
        const response = await apiService.getLandDeliveryDetails(shipmentNumber, containerNumber);
        const data = response.data;

        setDeliveryData(data);
        const initialDate = data.clientRequestedDeliveryDate ? moment(data.clientRequestedDeliveryDate) : null;
        const initialEmptyReady = data.emptyReady || false;
        const initialUrgent = data.urgent || false;

        setClientRequestedDeliveryDate(initialDate);
        setEmptyReady(initialEmptyReady);
        setUrgent(initialUrgent);
        setAuditLogs(data.auditLogs || []);

        // Store initial values for comparison
        setInitialValues({
          clientRequestedDeliveryDate: initialDate,
          emptyReady: initialEmptyReady,
          urgent: initialUrgent,
        });
      } catch (error) {
        console.error('Error fetching land delivery details:', error);
        // Handle error appropriately (e.g., show error message)
      } finally {
        setLoading(false);
      }
    };

    if (open && shipmentNumber && containerNumber) {
      fetchLandDeliveryDetails();
    }
  }, [open, shipmentNumber, containerNumber]);

  const handleEmptyReadyChange = (event) => {
    setEmptyReady(event.target.checked);
  };

  const handleUrgentChange = (event) => {
    setUrgent(event.target.checked);
  };

  const handleRequest = async () => {
    setLoading(true);
    try {
    const requestData = {
        shipmentNumber: shipmentNumber,
        containerNumber: containerNumber,
        clientRequestedDeliveryDate: clientRequestedDeliveryDate ? clientRequestedDeliveryDate.format() : null,
        emptyReady: emptyReady,
        urgent: urgent,
      };

      await apiService.updateLandDeliveryRequest(requestData);
      setLoading(false);
    } catch (error) {
      console.error('Error updating land delivery request:', error);
      // Handle error appropriately (e.g., show error notification)
    } finally {
      showSuccessSnackBar('The Delivery Request was updated successfully');
      onClose();
    }
  };

  // Check if values have changed
  const hasValuesChanged = () => {
    const dateChanged =
      (initialValues.clientRequestedDeliveryDate === null && clientRequestedDeliveryDate !== null) ||
      (initialValues.clientRequestedDeliveryDate !== null && clientRequestedDeliveryDate === null) ||
      (initialValues.clientRequestedDeliveryDate &&
        clientRequestedDeliveryDate &&
        !initialValues.clientRequestedDeliveryDate.isSame(clientRequestedDeliveryDate));

    const emptyReadyChanged = initialValues.emptyReady !== emptyReady;
    const urgentChanged = initialValues.urgent !== urgent;

    return dateChanged || emptyReadyChanged || urgentChanged;
  };

  // Check if at least one required field is filled
  const hasRequiredField =
    clientRequestedDeliveryDate !== null || emptyReady || urgent || initialValues.emptyReady || initialValues.urgent;

  // Disable button if no changes were made OR if neither field is filled
  const disableSaveButton = !hasValuesChanged() || !hasRequiredField;

  const renderAuditLogEntry = (log) => {
    const timestamp = log.createdDate ? moment(log.createdDate).format('DD/MM/YYYY hh:mm A') : '';
    const userName = log.userName || 'Unknown User';
    const action = log.action || '';

    // Helper function to format values (datetime or boolean)
    const formatValue = (value) => {
      if (!value) return null;

      // Check if it's a boolean value (True/False)
      const lowerValue = value.toString().toLowerCase();
      if (lowerValue === 'true') {
        return 'Checked';
      } else if (lowerValue === 'false') {
        return 'Unchecked';
      }

      // Try to parse the datetime format from API: M/D/YYYY h:mm:ss A Z
      const parsedDate = moment(value, ['M/D/YYYY h:mm:ss A Z', 'DD/M/YYYY HH:mm:ss Z', moment.ISO_8601], true);
      if (parsedDate.isValid()) {
        return parsedDate.format('DD/MM/YYYY hh:mm A');
      }

      return value;
    };

    // If property is null, show simple action message
    if (!log.property) {
      return (
        <span>
          <span className={classes.auditLogTimestamp}>
            {timestamp}
            {' - '}
          </span>
          <strong className={classes.auditLogUsername}>{userName}</strong> {action}
        </span>
      );
    }

    // If property exists, show detailed change with old and new values
    const formattedOldValue = formatValue(log.oldValue);
    const formattedNewValue = formatValue(log.newValue);

    return (
      <span>
        <span className={classes.auditLogTimestamp}>
          {timestamp}
          {' - '}
        </span>
        <span className={classes.auditLogMessage}>
          <strong className={classes.auditLogUsername}>{userName}</strong>
          {' changed '}
          <span className={classes.auditLogProperty}>{log.property}</span>
          {formattedOldValue && (
            <>
              {' from '}
              <strong>{formattedOldValue}</strong>
            </>
          )}
          {formattedNewValue && (
            <>
              {' to '}
              <strong>{formattedNewValue}</strong>
            </>
          )}
        </span>
      </span>
    );
  };

  if (!open) return null;

  return (
    <Paper className={classes.root}>
      {!loading && (
        <Box>
          <h4>Manage Delivery Request</h4>
          <IconButton aria-label="close" onClick={onClose} className={classes.closeButton}>
            <CloseIcon size="small" />
          </IconButton>
          <Divider className={classes.divider} />
        </Box>
      )}

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : (
        <Grid container spacing={2} className={classes.contentContainer}>
          <Grid item xs={12}>
            <div className={classes.readOnlySection}>
              {/* Container Number - Read Only */}
              <TextField label="Container Number" value={deliveryData?.containerNumber || containerNumber || ''} fullWidth margin="dense" disabled={true} />

              {/* Shipment Number - Read Only */}
              <TextField label="Shipment Number" value={deliveryData?.shipmentNumber || shipmentNumber || ''} fullWidth margin="dense" disabled={true} />
            </div>

            {/* Client Requested Delivery Date Section */}
            <div className={classes.section}>
              <KeyboardDateTimePicker
                label="Client Requested Delivery Date"
                value={clientRequestedDeliveryDate}
                onChange={setClientRequestedDeliveryDate}
                format="DD/MM/yyyy hh:mm a"
                fullWidth
                variant="inline"
                inputVariant="outlined"
              />
            </div>

            {/* Empty Ready Section */}
            <div className={classes.section}>
              <FormControl variant="outlined" className={classes.formControl + ' ' + classes.checkBlockControl + ' exclude'}>
                <FormControlLabel
                  className={classes.checkBox}
                  checked={emptyReady}
                  onChange={handleEmptyReadyChange}
                  control={<CustomCheckbox name="isEmptyReady" color="primary" />}
                  label="Empty Ready"
                />
              </FormControl>
              <br />
              <FormControl variant="outlined" className={classes.formControl + ' ' + classes.checkBlockControl + ' exclude'}>
                <FormControlLabel
                  className={classes.checkBox}
                  checked={urgent}
                  onChange={handleUrgentChange}
                  control={<CustomCheckbox name="isUrgent" color="primary" />}
                  label="Urgent Container"
                />
              </FormControl>
            </div>

            {/* Previous Values (Audit Log) Section */}
            {auditLogs.length > 0 && (
              <>
                <Box className={classes.auditLogHeader} onClick={() => setAuditLogExpanded(!auditLogExpanded)}>
                  <Typography className={classes.auditLogTitle}>History</Typography>
                  <IconButton
                    className={`${classes.expand} ${auditLogExpanded ? classes.expandOpen : ''}`}
                    aria-expanded={auditLogExpanded}
                    aria-label="show more"
                    size="small"
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                <Collapse in={auditLogExpanded} timeout="auto" unmountOnExit>
                  <div className={classes.auditLogContent}>
                    {auditLogs.map((log, index) => (
                      <div key={index} className={classes.auditLogItem}>
                        {renderAuditLogEntry(log)}
                      </div>
                    ))}
                  </div>
                </Collapse>
              </>
            )}

            {/* Info Box */}
            <div className={classes.infoBox}>
              <InfoIcon className={classes.infoIcon} />
              <Typography className={classes.infoText}>
                By clicking <strong>'Request'</strong>, an automated notification will be sent to the Verus Global Operations team for follow-up.
              </Typography>
            </div>

            {/* Buttons */}
            <Box className={classes.buttonsBox}>
              <MyButton theme={themeName} type="submit" disabled={disableSaveButton} className={classes.buttons} onClick={handleRequest} buttonType="primary">
                Request
              </MyButton>
              <MyButton theme={themeName} className={classes.buttons} buttonType="cancel" onClick={onClose}>
                Cancel
              </MyButton>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default LandDeliveryModal;
